import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './VehiclesList.css';

function VehiclesList() {
  const [vehicles, setVehicles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0, totalPages: 1 });
  const navigate = useNavigate();

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/vehicles', {
        params: {
          page,
          pageSize,
          search: searchQuery.trim(),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setVehicles(response.data.vehicles || []);
      setPagination(response.data.pagination || { page, pageSize, total: 0, totalPages: 1 });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load vehicles.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  return (
    <div className="vehicles-page app-fixed-page">
      <div className="vehicles-header app-page-header">
        <div className="app-title-group">
          <button className="back-link app-back-button" onClick={() => navigate('/')} aria-label="Back to home">
            Back
          </button>
          <div className="app-title-copy">
          <h2>Vehicles</h2>
          <p>View saved vehicles or add a new vehicle entry.</p>
          </div>
        </div>
        <div className="app-header-actions">
          <button className="btn-new-vehicle" onClick={() => navigate('/vehicles/new')}>
            + New Vehicle
          </button>
        </div>
      </div>

      <div className="vehicles-toolbar">
        <input
          className="vehicles-search"
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setPage(1);
            setSearchQuery(e.target.value);
          }}
          placeholder="Search by car number, VIN, make, model, body..."
        />
        <div className="vehicles-actions">
          <select
            className="vehicles-page-size"
            value={pageSize}
            onChange={(e) => {
              setPage(1);
              setPageSize(parseInt(e.target.value, 10));
            }}
          >
            <option value={10}>10 rows</option>
            <option value={20}>20 rows</option>
            <option value={50}>50 rows</option>
          </select>
          <button className="vehicles-action" onClick={fetchVehicles}>Refresh</button>
        </div>
      </div>

      <div className="app-scroll-results">
        <div className="vehicles-table-wrapper app-grid-scroll">
          {loading ? (
            <div className="vehicles-loading">Loading vehicles...</div>
          ) : error ? (
            <div className="vehicles-error">{error}</div>
          ) : (
            <table className="vehicles-table">
              <thead>
                <tr>
                  <th>Car #</th>
                  <th>Client</th>
                  <th>VIN</th>
                  <th>Make</th>
                  <th>Body</th>
                  <th>Model</th>
                  <th>Year</th>
                  <th>Engine</th>
                  <th>CC</th>
                  <th>Grade</th>
                  <th>Drive</th>
                  <th>Fuel</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="empty-message">
                      No vehicles found. Add a new vehicle to get started.
                    </td>
                  </tr>
                ) : (
                  vehicles.map((vehicle) => (
                    <tr key={vehicle.VehicleInfoId}>
                      <td>{vehicle.CarNumber || '-'}</td>
                      <td>{vehicle.ClientName || '-'}</td>
                      <td>{vehicle.VINNumber || '-'}</td>
                      <td>{vehicle.Make || '-'}</td>
                      <td>{vehicle.Body || '-'}</td>
                      <td>{vehicle.Model || '-'}</td>
                      <td>{vehicle.Year || '-'}</td>
                      <td>{vehicle.Engine || '-'}</td>
                      <td>{vehicle.CC || '-'}</td>
                      <td>{vehicle.Grade || '-'}</td>
                      <td>{vehicle.Drive || '-'}</td>
                      <td>{vehicle.Fuel || '-'}</td>
                      <td>
                        <button
                          className="vehicles-action vehicles-row-action"
                          onClick={() => navigate(`/vehicles/${vehicle.VehicleInfoId}/edit`)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {!loading && !error && (
          <div className="vehicles-pagination">
            <div className="vehicles-page-info">
              Showing page {pagination.page} of {pagination.totalPages} | Total rows: {pagination.total}
            </div>
            <div className="vehicles-page-buttons">
              <button
                className="vehicles-action"
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={pagination.page <= 1}
              >
                Previous
              </button>
              <button
                className="vehicles-action"
                onClick={() => setPage((prev) => Math.min(prev + 1, pagination.totalPages))}
                disabled={pagination.page >= pagination.totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default VehiclesList;
